import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Package, MapPin, FileText, AlertCircle, Check, Navigation, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { fetchUserLoads } from '../store/slices/loadSlice';
import { toast } from 'react-toastify';
import Modal from './Modal';
import api from '../utils/api';
import { getLoadDisplayId, getLoadUrlId } from '../utils/loadUtils';

// Import the same helper components from EditLoadModal
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-5 h-5 text-gray-600" />
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
  </div>
);

const DateTimeInput = ({ label, dateValue, timeValue, onDateChange, onTimeChange, disabled }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="grid grid-cols-2 gap-2">
      <input
        type="date"
        value={dateValue}
        onChange={(e) => onDateChange(e.target.value)}
        disabled={disabled}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
      <input
        type="time"
        value={timeValue}
        onChange={(e) => onTimeChange(e.target.value)}
        disabled={disabled}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
    </div>
  </div>
);

const Input = ({ label, type = "text", value, onChange, disabled }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
    />
  </div>
);

const TextArea = ({ label, value, onChange, disabled }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <textarea
      value={value}
      onChange={onChange}
      disabled={disabled}
      rows={3}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
    />
  </div>
);

const StopInput = ({ stop, onChange, onDelete, isFirst, isLast, disabled }) => (
  <div className="relative pl-4">
    <div className={`p-4 rounded-lg ${
      stop.stop_type === 'pickup' ? 'bg-blue-50' : 
      stop.stop_type === 'delivery' ? 'bg-green-50' : 
      'bg-gray-50'
    }`}>
      {/* Timeline indicator */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col items-center">
        <div className={`w-2.5 h-2.5 rounded-full ${
          stop.stop_type === 'pickup' ? 'bg-blue-500' : 
          stop.stop_type === 'delivery' ? 'bg-green-500' : 
          'bg-gray-400'
        }`} />
        {!isLast && <div className="w-0.5 flex-grow bg-gray-200 my-1" />}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <select
            value={stop.stop_type}
            onChange={(e) => onChange({ ...stop, stop_type: e.target.value })}
            disabled={disabled || isFirst || isLast}
            className="text-sm font-medium px-2 py-1 rounded-md border border-gray-300"
          >
            <option value="pickup">Pickup</option>
            <option value="stop">Stop</option>
            <option value="delivery">Delivery</option>
          </select>
          
          {!isFirst && !isLast && !disabled && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-red-500 hover:bg-red-50 rounded-md"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <DateTimeInput
          label="Schedule"
          dateValue={stop.date}
          timeValue={stop.time}
          onDateChange={(value) => onChange({ ...stop, date: value })}
          onTimeChange={(value) => onChange({ ...stop, time: value })}
        />

        <Input
          label="Address"
          value={stop.address}
          onChange={(e) => onChange({ ...stop, address: e.target.value })}
          disabled={disabled}
        />

        <div className="grid grid-cols-3 gap-2">
          <Input
            label="City"
            value={stop.city}
            onChange={(e) => onChange({ ...stop, city: e.target.value })}
            disabled={disabled}
          />
          <Input
            label="State"
            value={stop.state}
            onChange={(e) => onChange({ ...stop, state: e.target.value })}
            disabled={disabled}
          />
          <Input
            label="ZIP"
            value={stop.zip}
            onChange={(e) => onChange({ ...stop, zip: e.target.value })}
            disabled={disabled}
          />
        </div>

        <TextArea
          label="Stop Notes"
          value={stop.notes || ''}
          onChange={(e) => onChange({ ...stop, notes: e.target.value })}
          disabled={disabled}
        />

        {/* Add contact information section */}
        <div className="space-y-3 border-t border-gray-200 pt-3 mt-3">
          <h4 className="text-sm font-medium text-gray-700">Contact Information</h4>
          <Input
            label="Contact Name"
            value={stop.contact_name || ''}
            onChange={(e) => onChange({ ...stop, contact_name: e.target.value })}
            disabled={disabled}
          />
          <Input
            label="Contact Phone"
            value={stop.contact_phone || ''}
            onChange={(e) => onChange({ ...stop, contact_phone: e.target.value })}
            disabled={disabled}
          />
          <Input
            label="Contact Email"
            value={stop.contact_email || ''}
            onChange={(e) => onChange({ ...stop, contact_email: e.target.value })}
            disabled={disabled}
          />
          <TextArea
            label="Contact Instructions"
            value={stop.contact_instructions || ''}
            onChange={(e) => onChange({ ...stop, contact_instructions: e.target.value })}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  </div>
);

const ResubmitLoadModal = ({ load, isOpen, onClose }) => {
  const dispatch = useDispatch();
  
  // Initialize with future dates (next business day)
  const getNextBusinessDay = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Skip weekends
    if (tomorrow.getDay() === 0) tomorrow.setDate(tomorrow.getDate() + 1); // Sunday -> Monday
    if (tomorrow.getDay() === 6) tomorrow.setDate(tomorrow.getDate() + 2); // Saturday -> Monday
    
    return tomorrow.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    weight: load.weight,
    pieces: load.pieces,
    dimensions: load.dimensions || { length: '', width: '', height: '' },
    load_type: load.load_type,
    description: load.description || '',
    special_requirements: load.special_requirements || '',
    stops: load.stops ? load.stops.map(stop => ({
      ...stop,
      date: getNextBusinessDay(), // Update to future date
      zip: stop.zip_code || stop.zip,
    })) : [
      {
        id: 'pickup',
        stop_type: 'pickup',
        date: getNextBusinessDay(),
        time: load.pickup_time ? load.pickup_time.substring(0, 5) : '09:00',
        address: load.origin_address,
        city: load.origin_city,
        state: load.origin_state,
        zip: load.origin_zip,
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        contact_instructions: ''
      },
      {
        id: 'delivery',
        stop_type: 'delivery',
        date: getNextBusinessDay(),
        time: load.delivery_time ? load.delivery_time.substring(0, 5) : '17:00',
        address: load.destination_address,
        city: load.destination_city,
        state: load.destination_state,
        zip: load.destination_zip,
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        contact_instructions: ''
      }
    ]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Transform data for the create load endpoint
      const cleanedStops = formData.stops.map((stop, index) => {
        // Ensure time is in HH:MM format
        let formattedTime = stop.time;
        if (formattedTime && formattedTime.length > 5) {
          formattedTime = formattedTime.substring(0, 5);
        }
        if (!formattedTime || !/^\d{2}:\d{2}$/.test(formattedTime)) {
          formattedTime = '09:00'; // Default to 9 AM if invalid
        }
        
        return {
          sequence: index,
          type: stop.stop_type,
          address: stop.address,
          city: stop.city,
          state: stop.state,
          zip: stop.zip,
          date: stop.date,
          time: formattedTime,
          notes: stop.notes || '',
          business_name: stop.business_name || '',
          contact_name: stop.contact_name || '',
          contact_phone: stop.contact_phone || '',
          contact_email: stop.contact_email || '',
          contact_instructions: stop.contact_instructions || ''
        };
      });

      const pickupStop = formData.stops.find(s => s.stop_type === 'pickup') || formData.stops[0];
      const deliveryStop = formData.stops.find(s => s.stop_type === 'delivery') || formData.stops[formData.stops.length - 1];

      // Format times for pickup and delivery
      const formatTime = (time) => {
        if (!time) return '09:00';
        let formatted = time.length > 5 ? time.substring(0, 5) : time;
        return /^\d{2}:\d{2}$/.test(formatted) ? formatted : '09:00';
      };

      const newLoadData = {
        load_type: formData.load_type,
        weight: formData.weight,
        pieces: formData.pieces || null,
        description: formData.description || '',
        special_requirements: formData.special_requirements || '',
        ...(formData.load_type !== 'Full Truckload' && {
          dimensions: formData.dimensions
        }),
        origin_city: pickupStop.city,
        origin_state: pickupStop.state,
        origin_address: pickupStop.address,
        origin_zip: pickupStop.zip,
        pickup_date: pickupStop.date,
        pickup_time: formatTime(pickupStop.time),
        destination_city: deliveryStop.city,
        destination_state: deliveryStop.state,
        destination_address: deliveryStop.address,
        destination_zip: deliveryStop.zip,
        delivery_date: deliveryStop.date,
        delivery_time: formatTime(deliveryStop.time),
        stops: cleanedStops,
        original_load_id: getLoadUrlId(load)  // Pass the original load UUID/ID for resubmission tracking
      };

      const response = await api.post('/api/loads', newLoadData);
      
      if (response.status === 201) {
        toast.success('Load resubmitted successfully');
        onClose();
        // Refresh loads to show updated resubmitted status
        dispatch(fetchUserLoads({ page: 1, per_page: 10, status: 'expired' }));
        // Also refresh active loads to show the new load
        dispatch(fetchUserLoads({ page: 1, per_page: 10, status: 'active' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resubmit load');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStopChange = (index, updatedStop) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map((stop, i) => i === index ? updatedStop : stop)
    }));
  };

  const handleAddStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [
        ...prev.stops.slice(0, -1),
        {
          id: `stop-${Date.now()}`,
          stop_type: 'stop',
          date: getNextBusinessDay(),
          time: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          notes: ''
        },
        prev.stops[prev.stops.length - 1] // Keep delivery as last stop
      ]
    }));
  };

  const handleDeleteStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <RefreshCw className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Resubmit Expired Load</h2>
            <p className="text-sm text-gray-500">
              Original Load #{getLoadDisplayId(load)} - {load.origin_city}, {load.origin_state} to {load.destination_city}, {load.destination_state}
            </p>
          </div>
        </div>
      </div>

      {/* Info message */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-blue-50 border border-blue-200 rounded-lg"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-blue-500" />
          <p className="text-sm font-medium text-blue-700">
            This will create a new load with updated dates. Please review and modify the details as needed.
          </p>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Route Information Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <SectionHeader icon={Navigation} title="Route Information" />
            <button
              type="button"
              onClick={handleAddStop}
              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <Plus className="w-4 h-4" />
              Add Stop
            </button>
          </div>
          
          <div className="space-y-4">
            {formData.stops.map((stop, index) => (
              <StopInput
                key={stop.id}
                stop={stop}
                onChange={(updatedStop) => handleStopChange(index, updatedStop)}
                onDelete={() => handleDeleteStop(index)}
                isFirst={index === 0}
                isLast={index === formData.stops.length - 1}
                disabled={false}
              />
            ))}
          </div>
        </div>

        {/* Cargo Details Section */}
        <div className="space-y-4">
          <SectionHeader icon={Package} title="Cargo Details" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Input
              label="Load Type"
              value={formData.load_type}
              onChange={(e) => setFormData(prev => ({ ...prev, load_type: e.target.value }))}
            />
            <Input
              label="Weight (lbs)"
              type="number"
              value={formData.weight}
              onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
            />
            <Input
              label="Pieces"
              type="number"
              value={formData.pieces}
              onChange={(e) => setFormData(prev => ({ ...prev, pieces: e.target.value }))}
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Dimensions (inches)</label>
              <div className="grid grid-cols-3 gap-1">
                <input
                  type="number"
                  placeholder="L"
                  value={formData.dimensions.length}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, length: e.target.value }
                  }))}
                  className="block w-full px-2 py-2 text-sm border border-gray-300 rounded-md"
                />
                <input
                  type="number"
                  placeholder="W"
                  value={formData.dimensions.width}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, width: e.target.value }
                  }))}
                  className="block w-full px-2 py-2 text-sm border border-gray-300 rounded-md"
                />
                <input
                  type="number"
                  placeholder="H"
                  value={formData.dimensions.height}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, height: e.target.value }
                  }))}
                  className="block w-full px-2 py-2 text-sm border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="space-y-4">
          <SectionHeader icon={FileText} title="Additional Information" />
          <div className="space-y-4">
            <TextArea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextArea
              label="Special Requirements"
              value={formData.special_requirements}
              onChange={(e) => setFormData(prev => ({ ...prev, special_requirements: e.target.value }))}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:hover:bg-orange-600 transition-colors flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? (
              <>
                <motion.div
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                <span>Resubmitting...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Resubmit Load</span>
              </>
            )}
          </motion.button>
        </div>
      </form>
    </Modal>
  );
};

export default ResubmitLoadModal;
